import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  InputAdornment,
  LinearProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import {
  createContactGroup,
  importContactsFromCSV,
  addContactsToGroup,
  fetchContactGroups,
} from "../../redux/Slice/Contacts/contactGroupsSlice";
import { fetchContacts } from "../../redux/Slice/Contacts/contactsSlice";

// Groups (datasets) only support WhatsApp contacts — same rule as ContactGroupDetails.jsx.
const isWhatsAppContact = (contact) => (contact.platform || "whatsapp") === "whatsapp";

const parseFileOnClient = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const formatted = rows
          .slice(1) // skip header row
          .map((row) => ({
            name: row[0] || "Unknown",
            phoneNumberID: row[1] ? String(row[1]).trim().replace(/\D/g, "") : "",
          }))
          .filter((c) => c.name !== "Unknown" && c.phoneNumberID);
        resolve(formatted);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });

const AddDatasetPanel = ({ onCancel, onCreated }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.contactGroups);
  const { contacts } = useSelector((state) => state.contacts);

  const [name, setName] = useState("");
  const [mode, setMode] = useState(null); // null | "import" | "existing"
  const [csvFile, setCsvFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLabelFilter, setSelectedLabelFilter] = useState("");
  const [selectedContactIds, setSelectedContactIds] = useState([]);

  useEffect(() => {
    if (mode === "existing") {
      dispatch(fetchContacts({ page: 1, limit: 1000, sortDirection: "asc" }));
    }
  }, [mode, dispatch]);

  const availableContacts = useMemo(() => contacts.filter(isWhatsAppContact), [contacts]);

  const availableLabels = useMemo(() => {
    const labelSet = new Set();
    availableContacts.forEach((contact) => {
      if (Array.isArray(contact.labels)) {
        contact.labels.forEach((label) => labelSet.add(label));
      }
    });
    return Array.from(labelSet).sort();
  }, [availableContacts]);

  const filteredContacts = useMemo(() => {
    return availableContacts.filter((contact) => {
      if (selectedLabelFilter && !contact.labels?.includes(selectedLabelFilter)) {
        return false;
      }
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase();
      return contact.name?.toLowerCase().includes(term) || contact.phoneNumberID?.includes(term);
    });
  }, [availableContacts, searchTerm, selectedLabelFilter]);

  const toggleContact = (contactId) => {
    setSelectedContactIds((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const allowedExtensions = [".csv", ".xlsx", ".xls"];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error("Please select a valid CSV or Excel file (.csv, .xlsx, .xls).");
      return;
    }
    setCsvFile(file);
    setFileName(file.name);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Please enter a dataset name");
      return;
    }

    try {
      const group = await dispatch(
        createContactGroup({ name: name.trim(), description: "" })
      ).unwrap();

      if (mode === "import" && csvFile) {
        const parsedContacts = await parseFileOnClient(csvFile);
        if (parsedContacts.length === 0) {
          toast.error("No valid contacts found in the file.");
          return;
        }
        await dispatch(
          importContactsFromCSV({ groupId: group._id, contacts: parsedContacts })
        ).unwrap();
        toast.success(`Dataset "${name}" created and ${parsedContacts.length} contact(s) imported.`);
      } else if (mode === "existing" && selectedContactIds.length > 0) {
        await dispatch(
          addContactsToGroup({ groupId: group._id, contactIds: selectedContactIds })
        ).unwrap();
        toast.success(`Dataset "${name}" created with ${selectedContactIds.length} contact(s).`);
      } else {
        toast.success(`Dataset "${name}" created.`);
      }

      dispatch(fetchContactGroups());
      onCreated(group);
    } catch (error) {
      const msg = typeof error === "string" ? error : error?.message || "Failed to create dataset.";
      toast.error(msg);
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Add Dataset
        </Typography>
        <IconButton size="small" onClick={onCancel}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <TextField
          label="Dataset Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          size="small"
          margin="normal"
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight="500" gutterBottom>
          Add contacts (optional — you can also do this later)
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Button
            variant={mode === "import" ? "contained" : "outlined"}
            size="small"
            startIcon={<CloudUploadIcon />}
            onClick={() => setMode(mode === "import" ? null : "import")}
            sx={{ textTransform: "none", borderRadius: "2rem" }}
          >
            Import Contacts
          </Button>
          <Button
            variant={mode === "existing" ? "contained" : "outlined"}
            size="small"
            onClick={() => setMode(mode === "existing" ? null : "existing")}
            sx={{ textTransform: "none", borderRadius: "2rem" }}
          >
            Add Contact
          </Button>
        </Box>

        {mode === "import" && (
          <Box
            sx={{
              border: (theme) => `2px dashed ${theme.palette.primary.main}`,
              borderRadius: 2,
              padding: 2,
              textAlign: "center",
            }}
          >
            {!csvFile ? (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload a CSV or Excel file with contact name and phone number
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  size="small"
                  sx={{ mt: 1, textTransform: "none", borderRadius: "2rem" }}
                >
                  Choose File
                  <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={handleFileChange} />
                </Button>
              </>
            ) : (
              <>
                <Chip label={fileName} color="success" variant="outlined" sx={{ mb: 1 }} />
                <Box>
                  <Button
                    size="small"
                    onClick={() => {
                      setCsvFile(null);
                      setFileName("");
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Remove
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}

        {mode === "existing" && (
          <Box>
            <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
              <TextField
                placeholder="Search contacts by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
              {availableLabels.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Label</InputLabel>
                  <Select
                    value={selectedLabelFilter}
                    label="Label"
                    onChange={(e) => setSelectedLabelFilter(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All Labels</em>
                    </MenuItem>
                    {availableLabels.map((label) => (
                      <MenuItem key={label} value={label}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            {selectedContactIds.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                {selectedContactIds.length} selected
              </Typography>
            )}
            <List
              dense
              sx={{
                maxHeight: 260,
                overflowY: "auto",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {filteredContacts.length === 0 ? (
                <ListItemButton disabled>
                  <ListItemText primary="No contacts found" />
                </ListItemButton>
              ) : (
                filteredContacts.map((contact) => (
                  <ListItemButton key={contact._id} onClick={() => toggleContact(contact._id)}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Checkbox
                        edge="start"
                        checked={selectedContactIds.includes(contact._id)}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    </ListItemIcon>
                    <ListItemText primary={contact.name || "Unknown"} secondary={contact.phoneNumberID} />
                  </ListItemButton>
                ))
              )}
            </List>
          </Box>
        )}

        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 2 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleCreate} variant="contained" disabled={loading || !name.trim()}>
          Create Dataset
        </Button>
      </Box>
    </Box>
  );
};

export default AddDatasetPanel;
